import React, { useEffect, useRef, useState } from 'react';

const domains = [
  {
    icon: 'ri-eye-line',
    title: 'Computer Vision & Medical AI',
    summary: 'Production systems for clinical and industrial imaging — from data pipeline to AWS deployment.',
    tools: ['PyTorch', 'OpenCV', 'Grad-CAM', 'AWS Rekognition', 'Medical Imaging', 'Segmentation'],
    proof: 'M-BOT (30k patients/day) · OsteoDoc (IEEE 2024, 0.91 AUC)',
    color: 'var(--first-color)',
  },
  {
    icon: 'ri-robot-line',
    title: 'AI Agents & LLM Systems',
    summary: 'Structured multi-step AI pipelines — outreach platforms, avatars, reflection assistants, and procurement agents.',
    tools: ['OpenAI GPT-4o', 'FastAPI', 'Pydantic', 'D-ID Streaming', 'Streamlit', 'Prompt Engineering'],
    proof: 'Gluon · Kris AI Avatar · IFS Assistant · Tender Pipeline',
    color: 'var(--profile-blue)',
  },
  {
    icon: 'ri-seedling-line',
    title: 'Reinforcement Learning & Simulation',
    summary: 'RL environments and simulation workflows for real-world decision support in agriculture and research.',
    tools: ['Stable-Baselines3', 'PyTorch', 'APSIM', 'CYCLES', 'WandB', 'Gymnasium'],
    proof: 'CyclesGym (MS Thesis) · APGym (AgriTech RL)',
    color: 'var(--profile-olive)',
  },
  {
    icon: 'ri-stack-line',
    title: 'Full-Stack AI Products',
    summary: 'End-to-end product engineering from database schema to React frontend — Supabase, FastAPI, Docker.',
    tools: ['React', 'TypeScript', 'Node.js', 'FastAPI', 'Supabase', 'Docker', 'WebSockets'],
    proof: 'Gluon · Campfire Backend · CyclesGym Demo',
    color: 'var(--profile-oxblood)',
  },
  {
    icon: 'ri-cloud-line',
    title: 'Cloud & MLOps',
    summary: 'Cloud infrastructure for AI workloads — AWS pipelines, containerisation, experiment tracking, and CI-ready deploys.',
    tools: ['AWS (SageMaker, Rekognition, S3, Transcribe)', 'Docker', 'Boto3', 'Git', 'Selenium', 'tqdm'],
    proof: 'M-BOT AWS deploy · Content Moderation · Tender Pipeline',
    color: 'var(--profile-copper)',
  },
  {
    icon: 'ri-bar-chart-grouped-line',
    title: 'Research & Publication',
    summary: 'Peer-reviewed research, experiment rigor, and translating findings into communicable evidence.',
    tools: ['IEEE Xplore', 'Grad-CAM XAI', 'Ablation Studies', 'Matplotlib', 'Pandas', 'NumPy'],
    proof: 'OsteoDoc — Best Paper ICODT2 · CyclesGym thesis evidence packs',
    color: 'var(--profile-blue)',
  },
];

const Skills = () => {
  const [visible, setVisible] = useState([]);
  const cardRefs = useRef([]);

  useEffect(() => {
    const observers = cardRefs.current.map((el, i) => {
      if (!el) return null;
      const obs = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting) {
          setVisible(prev => prev.includes(i) ? prev : [...prev, i]);
          obs.disconnect();
        }
      }, { threshold: 0.15 });
      obs.observe(el);
      return obs;
    });
    return () => observers.forEach(o => o && o.disconnect());
  }, []);

  return (
    <section className="skills section" id="skills">
      <h2 className="section__title-1 reveal-up"><span>Expertise.</span></h2>

      <p className="skills__intro container reveal-up" style={{ animationDelay: '0.1s' }}>
        Four years building AI systems from research prototype to production — across healthcare, agritech, edtech,
        enterprise automation, and creative AI. Each domain below reflects shipped work, not just listed tools.
      </p>

      <div className="skills__container container grid">
        {domains.map((d, i) => (
          <div
            className={`skills__card skills__card--anim${visible.includes(i) ? ' skills__card--visible' : ''}`}
            key={i}
            style={{ '--card-accent': d.color, transitionDelay: `${(i % 3) * 100}ms` }}
            ref={el => cardRefs.current[i] = el}
          >
            <div className="skills__card-icon">
              <i className={d.icon}></i>
            </div>
            <h3 className="skills__card-title">{d.title}</h3>
            <p className="skills__card-summary">{d.summary}</p>
            <div className="skills__tools">
              {d.tools.map(t => (
                <span className="skills__tool" key={t}>{t}</span>
              ))}
            </div>
            <div className="skills__proof">
              <span className="skills__proof-label">Applied in projects</span>
              <span className="skills__proof-list">
                <i className="ri-checkbox-circle-line"></i> {d.proof}
              </span>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default Skills;
